import {
  ExternalIngestionDocumentFragment,
  ExternalValidationReportDataFragment,
  GetIngestionDocumentFragment,
  IngestionDocumentValidationErrorReviewFragment,
  IngestionDocumentValidationFragment,
  RuleType,
  VariableSource,
} from '@client/graphql/__generated__/types';
import { TITLE_TERMINATORS, titleCase } from 'title-case';
import { getFieldLevelComparisonData } from './fieldComparison.utils';
import { createFieldKeyToLabelMapping } from './fieldMapping.utils';

export interface ValidationItem {
  message: string;
  ruleUuid: string;
  ruleType?: RuleType;
  pageNumber?: number;
  contractDocumentUuid?: string;
  mappingKeys?: string[];
  ingestionDocumentValidationId?: string;
  ruleFormula?: string;
  isDocumentValidation?: boolean;
  isPipelineValidation?: boolean;
  inputValues?: {
    variableSource?: VariableSource;
    fieldKey: string;
    value: string;
  }[];
}

export interface ValidationResult {
  ruleUuid: string;
  output: boolean;
  message: string;
  ruleType?: RuleType;
  pageNumber?: number;
  contractDocumentUuid?: string;
  fieldMappingKey?: string;
  ruleFormula?: string;
  isDocumentValidation?: boolean;
  isPipelineValidation?: boolean;
  inputValues?: {
    variableSource?: VariableSource;
    fieldKey: string;
    value: string;
  }[];
  documentVersionFieldUuid?: string;
}

/**
 * Extracts validation results (errors and successes) for a specific document
 *
 * @param document - The ingestion document to get validation results for
 * @param validationData - Optional validation data containing document validations
 * @param contractDocumentId - Optional specific contract document ID to filter results
 * @returns Object containing arrays of error and success validation items
 *
 * @example
 * const { errors, successes } = getValidationResultsForDocument(
 *   document,
 *   validationData,
 *   'contract-123'
 * );
 * Log.info(`Found ${errors.length} errors and ${successes.length} successes`);
 */
export function getValidationResultsForDocument(
  document: GetIngestionDocumentFragment | ExternalIngestionDocumentFragment,
  validationData?: ExternalValidationReportDataFragment | null,
  contractDocumentId?: string
): { errors: ValidationItem[]; successes: ValidationItem[] } {
  if (!document || !validationData?.documentValidations) {
    return { errors: [], successes: [] };
  }

  // Get contract document UUIDs from the document
  const contractDocumentUuids = new Set(
    document.contractDocuments?.map((cd: { id: string }) => cd.id) || []
  );

  if (contractDocumentUuids.size === 0) {
    return { errors: [], successes: [] };
  }

  const errors: ValidationItem[] = [];
  const successes: ValidationItem[] = [];
  const errorIds = new Set<string>();
  const successIds = new Set<string>();

  // Process each document validation
  validationData.documentValidations.forEach((docValidation) => {
    // Handle the dynamic Record structure from GraphQL
    const aiValidationResults = docValidation.aiValidationResults as unknown as
      | ValidationResult[]
      | undefined;
    const validationResults = docValidation.validationResults as unknown as
      | ValidationResult[]
      | undefined;

    // Prioritize AI validation results, but fall back to regular validation results if AI results are empty
    const resultsToProcess =
      aiValidationResults && aiValidationResults.length > 0
        ? aiValidationResults
        : validationResults;

    if (resultsToProcess) {
      resultsToProcess.forEach((result) => {
        // Only include results for this document's contract documents
        if (
          result.contractDocumentUuid &&
          contractDocumentUuids.has(result.contractDocumentUuid)
        ) {
          // If contractDocumentId is provided, filter by specific contract document
          if (
            contractDocumentId &&
            result.contractDocumentUuid !== contractDocumentId
          ) {
            return;
          }

          const mappingKeys =
            result.inputValues?.reduce((acc, val) => {
              if (val.variableSource === VariableSource.FIELD) {
                acc.push(val.fieldKey);
              }

              return acc;
            }, [] as string[]) ||
            (result.fieldMappingKey ? [result.fieldMappingKey] : []);

          const item: ValidationItem = {
            message: result.message,
            ruleUuid: result.ruleUuid,
            ruleType: result.ruleType,
            pageNumber: result.pageNumber,
            contractDocumentUuid: result.contractDocumentUuid,
            mappingKeys,
            inputValues: result.inputValues,
            ingestionDocumentValidationId: docValidation.id,
            ruleFormula: result.ruleFormula,
            isDocumentValidation: result.isDocumentValidation,
            isPipelineValidation: result.isPipelineValidation,
          };

          if (result.output && !errorIds.has(item.ruleUuid)) {
            errors.push(item);

            if (item.ruleUuid) {
              errorIds.add(item.ruleUuid);
            }
          }

          if (!result.output && !successIds.has(item.ruleUuid)) {
            successes.push(item);

            if (item.ruleUuid) {
              successIds.add(item.ruleUuid);
            }
          }
        }
      });
    }
  });

  return { errors, successes };
}

/**
 * Gets error and success counts for a specific contract document.
 *
 * This function filters out hidden errors based on the following matching rules:
 * - A review hides an error when ALL of these conditions are met:
 *   1. review.ruleUuid matches the error's ruleUuid
 *   2. review.contractDocumentUuid equals the provided contractDocumentUuid
 *   3. review.ingestionDocumentValidation.id equals the current validation's id (validation-version-specific hide)
 *   4. review.isHidden is true
 *
 * @param contractDocumentUuid - The UUID of the contract document to count errors for
 * @param validationData - The validation data containing document validations and results
 * @param hiddenDocumentValidationReviews - Optional array of review records marking errors as hidden
 * @returns Object containing counts of { errors, successes }
 */
function getErrorAndSuccessCountsForContractDocument(
  contractDocumentUuid: string,
  validationData: ExternalValidationReportDataFragment | null,
  hiddenDocumentValidationReviews?: IngestionDocumentValidationErrorReviewFragment[]
): { errors: number; successes: number } {
  if (!validationData?.documentValidations) {
    return { errors: 0, successes: 0 };
  }

  let errorCount = 0;
  let successCount = 0;

  validationData.documentValidations.forEach((docValidation) => {
    const aiValidationResults = docValidation.aiValidationResults as unknown as
      | ValidationResult[]
      | undefined;
    const validationResults = docValidation.validationResults as unknown as
      | ValidationResult[]
      | undefined;

    const resultsToProcess =
      aiValidationResults && aiValidationResults.length > 0
        ? aiValidationResults
        : validationResults;

    if (resultsToProcess) {
      resultsToProcess.forEach((result) => {
        if (result.contractDocumentUuid === contractDocumentUuid) {
          if (result.output) {
            // Check if this error is hidden based on matching ruleUuid, contractDocumentUuid, and validation ID
            const isHidden = hiddenDocumentValidationReviews?.some(
              (review) =>
                review.ruleUuid === result.ruleUuid &&
                review.contractDocumentUuid === contractDocumentUuid &&
                review.ingestionDocumentValidation?.id === docValidation.id &&
                review.isHidden
            );

            // Only count if not hidden
            if (!isHidden) {
              errorCount++;
            }
          } else {
            successCount++;
          }
        }
      });
    }
  });

  return { errors: errorCount, successes: successCount };
}

/**
 * Gets error and success counts for an entire ingestion document (all its contract documents)
 */
export function getErrorAndSuccessCountsForIngestionDocument(
  document: ExternalIngestionDocumentFragment,
  validationData: ExternalValidationReportDataFragment | null,
  contractDocumentId?: string,
  hiddenDocumentValidationReviews?: IngestionDocumentValidationErrorReviewFragment[]
): { errors: number; successes: number } {
  if (!document.contractDocuments || !validationData?.documentValidations) {
    return { errors: 0, successes: 0 };
  }

  // If contractDocumentId is provided, only count for that specific contract document
  if (contractDocumentId) {
    return getErrorAndSuccessCountsForContractDocument(
      contractDocumentId,
      validationData,
      hiddenDocumentValidationReviews
    );
  }

  let totalErrorCount = 0;
  let totalSuccessCount = 0;

  document.contractDocuments.forEach((contractDoc) => {
    const counts = getErrorAndSuccessCountsForContractDocument(
      contractDoc.id,
      validationData,
      hiddenDocumentValidationReviews
    );
    totalErrorCount += counts.errors;
    totalSuccessCount += counts.successes;
  });

  return { errors: totalErrorCount, successes: totalSuccessCount };
}

/**
 * Converts a field key into a human-readable label.
 *
 * Transforms nested field keys by replacing structural characters with readable separators
 * and applying title case formatting.
 *
 * @param key - The field key to transform (e.g., "buyer[0].name_first")
 * @returns A human-readable label (e.g., "Buyer > 0 Name | First")
 *
 * @example
 * friendlifyLabel("buyer[0].name_first") // "Buyer > 0 Name | First"
 * friendlifyLabel("seller.address_street") // "Seller | Address Street"
 */
const friendlifyLabel = (key: string) => {
  const replacedKey = key
    .replaceAll(']', ' ')
    .replaceAll('_', ' ')
    .replaceAll('.', ' | ')
    .replaceAll('[', ' > ')
    .replaceAll('  ', ' ');

  return titleCase(replacedKey, {
    titleTerminators: new Set([...TITLE_TERMINATORS, '|', '>']),
  });
};

/**
 * Groups and formats input values for display in tooltips
 */
export function formatInputValuesForTooltip(
  inputValues?: {
    variableSource?: VariableSource;
    fieldKey: string;
    value: string;
  }[],
  document?: GetIngestionDocumentFragment | null,
  variant: 'internal' | 'external' = 'external'
): {
  fromContract: Array<{ label: string; value: string }>;
  externalReference: Array<{ label: string; value: string }>;
} {
  if (!inputValues || inputValues.length === 0) {
    return { fromContract: [], externalReference: [] };
  }

  const fieldKeyToLabel = createFieldKeyToLabelMapping(document);

  const fromContract: Array<{ label: string; value: string }> = [];
  const externalReference: Array<{ label: string; value: string }> = [];

  const shouldShowInputValue = (fieldKey: string) => {
    if (fieldKey === 'ingestion.documentFormNames') {
      return variant === 'internal';
    }

    return true;
  };

  inputValues.forEach((input) => {
    if (!shouldShowInputValue(input.fieldKey)) {
      return;
    }
    const friendlyLabel =
      fieldKeyToLabel[input.fieldKey] || friendlifyLabel(input.fieldKey);
    const friendlyValue =
      !input.value || input.value === 'undefined' ? '-' : input.value;
    const displayItem = { label: friendlyLabel, value: friendlyValue };

    const source = input.variableSource ?? VariableSource.FIELD;

    if (source === VariableSource.FIELD) {
      if (fieldKeyToLabel[input.fieldKey]) {
        fromContract.push(displayItem);
      }
    } else {
      // All other sources are considered external reference data
      externalReference.push(displayItem);
    }
  });

  return { fromContract, externalReference };
}

export interface ImprovedField {
  fieldKey: string;
  fieldLabel: string;
  reductionCount: number;
}

export interface AnalysisWithFields {
  analysis: string;
  contractDocumentUuid: string;
  relatedFieldKeys?: string[];
}

export interface ValidationComparisonData {
  aiErrorCount: number;
  regularErrorCount: number;
  aiWarningCount: number;
  regularWarningCount: number;
  reductionPercentage: number;
  hasAiData: boolean;
  latestValidationDate?: string;
  aiAnalysisData?: string[];
  improvedFieldsList?: ImprovedField[];
  aiAnalysisWithFields?: AnalysisWithFields[];
}

/**
 * Calculates percentage reduction in validation issues
 */
function calculateReductionPercentage(
  totalRegularIssues: number,
  totalAiIssues: number
): number {
  if (totalRegularIssues === 0) return 0;

  return Math.round(
    ((totalRegularIssues - totalAiIssues) / totalRegularIssues) * 100
  );
}

/**
 * Extracts fields that were improved by AI processing
 */
function extractImprovedFields(
  fieldComparisons: Array<{
    fieldKey: string;
    fieldLabel?: string;
    changeType: string;
    reductionCount: number;
  }>
): ImprovedField[] {
  return fieldComparisons
    .filter(
      (field) =>
        field.changeType === 'removed' ||
        (field.changeType === 'modified' && field.reductionCount > 0)
    )
    .map((field) => ({
      fieldKey: field.fieldKey,
      fieldLabel: field.fieldLabel || field.fieldKey,
      reductionCount: field.reductionCount,
    }));
}

/**
 * Associates AI analysis insights with related field keys
 */
function buildAnalysisWithFields(
  document: GetIngestionDocumentFragment,
  validationForDocument: IngestionDocumentValidationFragment,
  fieldComparisons: Array<{
    fieldKey: string;
    changeType: string;
    reductionCount: number;
    aiResults: ValidationResult[];
  }>
): AnalysisWithFields[] {
  const analysisWithFields: AnalysisWithFields[] = [];

  const analyses = validationForDocument.aiAnalysis as
    | Array<{
        contractDocumentUuid: string;
        analysis: string;
      }>
    | undefined;

  if (!analyses) return [];

  analyses.forEach((analysisItem) => {
    const relatedFieldKeys = fieldComparisons
      .filter(
        (field) =>
          (field.changeType === 'removed' ||
            (field.changeType === 'modified' && field.reductionCount > 0)) &&
          field.aiResults.some(
            (result) =>
              result.contractDocumentUuid === analysisItem.contractDocumentUuid
          )
      )
      .map((field) => field.fieldKey);

    analysisWithFields.push({
      analysis: analysisItem.analysis,
      contractDocumentUuid: analysisItem.contractDocumentUuid,
      relatedFieldKeys:
        relatedFieldKeys.length > 0 ? relatedFieldKeys : undefined,
    });
  });

  return analysisWithFields;
}

/**
 * Gets the validation for a specific IngestionDocument.
 */
export function getValidationForDocument(
  document: GetIngestionDocumentFragment,
  validationData?: ExternalValidationReportDataFragment | null
): IngestionDocumentValidationFragment | null {
  if (!validationData?.documentValidations) {
    return null;
  }

  const validations = validationData.documentValidations.filter(
    (validation) => document.id === validation.ingestionDocument.id
  );

  if (validations.length === 0) {
    return null;
  }

  if (validations.length > 1) {
    // eslint-disable-next-line no-console
    console.warn(`Multiple validations found for document ${document.id}.`);
  }

  return validations[0];
}

/**
 * Compares AI vs regular validation results for a specific document
 */
export function getValidationComparisonData(
  document: GetIngestionDocumentFragment,
  validationForDocument: IngestionDocumentValidationFragment
): ValidationComparisonData | null {
  const comparisonData: ValidationComparisonData = {
    aiErrorCount: 0,
    aiWarningCount: 0,
    regularErrorCount: 0,
    regularWarningCount: 0,
    reductionPercentage: 0,
    hasAiData: false,
  };

  let hasAiProcessing = false;
  let mostRecentValidationDate: string | undefined;

  if (!!validationForDocument.aiValidationResults?.length) {
    hasAiProcessing = true;
  }

  if (validationForDocument.updatedAt) {
    mostRecentValidationDate = validationForDocument.updatedAt as string;
  }

  comparisonData.aiErrorCount = validationForDocument.aiErrorCount;
  comparisonData.aiWarningCount = validationForDocument.aiWarningCount;
  comparisonData.regularErrorCount = validationForDocument.errorCount;
  comparisonData.regularWarningCount = validationForDocument.warningCount;

  const totalRegularIssues =
    comparisonData.regularErrorCount + comparisonData.regularWarningCount;
  const totalAiIssues =
    comparisonData.aiErrorCount + comparisonData.aiWarningCount;
  const improvementPercentage = calculateReductionPercentage(
    totalRegularIssues,
    totalAiIssues
  );

  const fieldComparisonData = getFieldLevelComparisonData(
    document,
    validationForDocument
  );
  const improvedFields = extractImprovedFields(
    fieldComparisonData.fieldComparisons
  );
  const analysisWithFieldContext = buildAnalysisWithFields(
    document,
    validationForDocument,
    fieldComparisonData.fieldComparisons
  );

  return {
    ...comparisonData,
    reductionPercentage: improvementPercentage,
    hasAiData: hasAiProcessing,
    latestValidationDate: mostRecentValidationDate,
    improvedFieldsList: improvedFields.length > 0 ? improvedFields : undefined,
    aiAnalysisWithFields:
      analysisWithFieldContext.length > 0
        ? analysisWithFieldContext
        : undefined,
  };
}

/**
 * Gets validation timestamps from document validations
 */
export function getValidationTimestamps(
  validationForDocument: IngestionDocumentValidationFragment
): {
  createdAt?: string;
  updatedAt?: string;
  isUpToDate: boolean;
} {
  if (!validationForDocument) {
    return { isUpToDate: false };
  }

  // TODO: BLO-2146: use validatedAt and aiValidationUpdatedAt
  // Get the most recent validation
  const mostRecent = validationForDocument.updatedAt as string;

  if (!mostRecent) {
    return { isUpToDate: false };
  }

  const updatedAt = mostRecent;
  const createdAt = validationForDocument.createdAt as string;

  // Consider validation up to date if it's less than 24 hours old
  const isUpToDate = updatedAt
    ? new Date().getTime() - new Date(updatedAt).getTime() < 24 * 60 * 60 * 1000
    : false;

  return {
    createdAt,
    updatedAt,
    isUpToDate,
  };
}

export function hasDocumentBeenValidated(
  document: ExternalIngestionDocumentFragment,
  validationData: ExternalValidationReportDataFragment | null
): boolean {
  if (!validationData?.documentValidations) {
    return false;
  }

  return validationData.documentValidations.some((docValidation) => {
    const aiResults = docValidation.aiValidationResults as unknown as
      | { ingestionDocumentUuid?: string }[]
      | undefined;
    const regularResults = docValidation.validationResults as unknown as
      | { ingestionDocumentUuid?: string }[]
      | undefined;
    const results = aiResults?.length ? aiResults : regularResults;

    return results?.some(
      (result) => result.ingestionDocumentUuid === document.id
    );
  });
}

export function hasIngestionDocumentBeenClassified(
  document: ExternalIngestionDocumentFragment
): boolean {
  return (document.contractDocuments?.length || 0) > 0;
}

/**
 * Gets display name for a contract document
 *
 * @param contractDoc - The contract document to get display name for
 * @returns Formatted display name like "Form - Name" or fallback to category
 */
export function getContractDocDisplayName(contractDoc?: {
  documentCategory?: string;
  documentVersion?: {
    document?: {
      form?: string;
      name?: string;
    };
  };
}): string {
  const docForm = contractDoc?.documentVersion?.document?.form;
  const docName = contractDoc?.documentVersion?.document?.name;
  const fallbackName = contractDoc?.documentCategory;

  if (docForm && docName) {
    return `${docForm} - ${docName}`;
  }

  return fallbackName || 'Unknown Document';
}
